import React, { useState, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { toast } from 'react-toastify';

import { useFiles } from '~/hooks/files';

import { DropContainer, UploadMessage } from './styles';

const FILE_UPLOAD_MAX_SIZE = 15 * 1024 * 1024;

interface FileUploadProps {
  formFieldId?: string;
  customFileTypes?: string[];
  customMessage?: string;
  multiple?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  formFieldId,
  customFileTypes,
  customMessage,
  multiple = true,
}) => {
  const [maxSize] = useState<number>(FILE_UPLOAD_MAX_SIZE);
  const { handleUpload } = useFiles();

  const fileTypes = [
    'image/*',
    'video/*',
    'audio/*',
    'text/*',
    '*.zip',
    'application/xml',
    'application/zip',
    'application/pdf',
    'application/octet-stream',
    'application/msword',
    'application/json',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.presentation',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/x-rar-compressed',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/x-7z-compressed',
    'application/x-zip-compressed',
    'application/zip',
    'application/vnd.rar',
  ];

  const onDrop = useCallback(
    (files) => {
      handleUpload(files, formFieldId);
    },
    [formFieldId, handleUpload],
  );

  const onDropRejected = useCallback((customErrors: FileRejection[]) => {
    customErrors.forEach((customError) => {
      const errorFinded = customError.errors.find(
        (error) =>
          error.code === 'file-too-large' || error.code === 'file-invalid-type',
      );

      if (errorFinded)
        toast.warn('Aviso, um ou mais arquivos inválidos ou ultrapassam 10MB.');
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize,
    multiple,
    accept: customFileTypes ?? fileTypes,
  });

  const renderDragMessage = useCallback(() => {
    if (!isDragActive) {
      return (
        <UploadMessage>
          {customMessage ?? 'Clique ou arraste arquivos aqui (10mb max)...'}
        </UploadMessage>
      );
    }

    return <UploadMessage type="success">Solte os arquivos aqui</UploadMessage>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragActive]);

  return (
    <DropContainer {...getRootProps()}>
      <input {...getInputProps()} />
      {renderDragMessage()}
    </DropContainer>
  );
};

export default FileUpload;
